/** What a room's follow-ups read and do, for the computer's tab and Season's: the automations on it and a switch each. */
import { useEffect, useState } from "react";
import type { AutomationSummary } from "../../../shared/types";
import { useToast } from "../../components/ui/Toast";
import { ApiError, api } from "../../lib/api";

/** People anywhere in the automation right now. */
export function inNow(a: Pick<AutomationSummary, "line">): number {
  return Object.values(a.line.here).reduce((sum, h) => sum + h.count, 0);
}

/** The automations that run on this room. `null` while they load. */
export function useRoomAutomations(roomId: string) {
  const [autos, setAutos] = useState<AutomationSummary[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    api.automations
      .list()
      .then((r) => alive && setAutos(r.automations.filter((a) => a.rooms.some((x) => x.webinar_id === roomId))))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [roomId]);
  const put = (next: AutomationSummary) => setAutos((was) => (was ?? []).map((x) => (x.id === next.id ? next : x)));
  return { autos, failed, put };
}

/** Is it on for this room, and how many are: an automation that runs counts when this room has it switched on. */
export function onHere(a: AutomationSummary, roomId: string): boolean {
  return a.status === "on" && !!a.rooms.find((r) => r.webinar_id === roomId)?.enabled;
}

/** One automation's switch for this room alone. */
export function useAutomationSwitch(a: AutomationSummary, roomId: string, onSaved: (a: AutomationSummary) => void) {
  const toast = useToast();
  const here = a.rooms.find((r) => r.webinar_id === roomId);
  const [on, setOn] = useState(!!here?.enabled);
  const [busy, setBusy] = useState(false);
  useEffect(() => setOn(!!here?.enabled), [here?.enabled]);
  const flip = (next: boolean) => {
    if (busy) return;
    setOn(next);
    setBusy(true);
    api.automations
      .saveWebinars(a.id, { webinars: [{ webinar_id: roomId, enabled: next }] })
      .then(() => {
        onSaved({ ...a, rooms: a.rooms.map((r) => (r.webinar_id === roomId ? { ...r, enabled: next } : r)) });
        toast({ text: `${a.name} is ${next ? "on" : "off"} for this room`, icon: next ? "check" : "pause" });
      })
      .catch((err: unknown) => {
        setOn(!next);
        toast(err instanceof ApiError ? err.message : "That didn't save. Try again.");
      })
      .finally(() => setBusy(false));
  };
  return { on, busy, flip, running: a.status === "on", now: inNow(a) };
}
